<script setup lang="ts">
	import { isService } from '@/config/services'
	import { computed, ref } from 'vue'

	import { ServiceTypes } from '@/enums/serviceTypes'

	import { Request } from '@/modules/trips/interfaces/cef'
	import type { FlightRequestDetail } from '@/modules/trips/interfaces/tripDetail'
	import type { User } from '@/modules/users/interfaces/user'

	type ServiceType = 'Hotel' | 'Bus' | 'Plane' | 'Car'

	interface Props {
		linkedService?: Request[] | Request | null
		serviceType?: ServiceType
		linkedFlight?: FlightRequestDetail | null
		member?: User | null
		travelers?: User[] | null
		loadingMember?: boolean
	}

	interface Emits {
		(event: 'unlink'): void
	}

	const props = withDefaults(defineProps<Props>(), {
		serviceType: 'Plane',
		loadingMember: false,
	})

	const emit = defineEmits<Emits>()

	const showDismissWarning = ref(false)

	function fmtDate(dateStr: string): string {
		if (!dateStr) return ''
		return new Date(dateStr).toLocaleDateString('es-MX', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
		})
	}

	const services = computed<Request[]>(() => {
		if (!props.linkedService && !props.linkedFlight) return []
		const data = props.linkedService ?? props.linkedFlight
		return (Array.isArray(data) ? data : [data]) as Request[]
	})

	const serviceConfig = computed(() => {
		const config: Record<ServiceType, any> = {
			Plane: {
				borderColor: 'border-indigo-200',
				bgColor: 'bg-indigo-50',
				iconBgColor: 'bg-indigo-100',
				iconTextColor: 'text-indigo-600',
				textColor: 'text-indigo-800',
				accentColor: 'text-indigo-400',
				icon: 'flight',
				message: 'Este boleto se vinculará',
			},

			Hotel: {
				borderColor: 'border-blue-200',
				bgColor: 'bg-blue-50',
				iconBgColor: 'bg-blue-100',
				iconTextColor: 'text-blue-600',
				textColor: 'text-blue-800',
				accentColor: 'text-blue-400',
				icon: 'apartment',
				message: 'Esta reserva se vinculará',
			},

			Bus: {
				borderColor: 'border-amber-200',
				bgColor: 'bg-amber-50',
				iconBgColor: 'bg-amber-100',
				iconTextColor: 'text-amber-600',
				textColor: 'text-amber-800',
				accentColor: 'text-amber-400',
				icon: 'directions_bus',
				message: 'Este autobús se vinculará',
			},

			Car: {
				borderColor: 'border-slate-200',
				bgColor: 'bg-slate-50',
				iconBgColor: 'bg-slate-100',
				iconTextColor: 'text-slate-600',
				textColor: 'text-slate-800',
				accentColor: 'text-slate-400',
				icon: 'directions_car',
				message: 'Este automóvil se vinculará',
			},
		}

		return config[props.serviceType] || config.Plane
	})

	// Los ids de `services` estaban hardcodeados aquí y en dos sitios más de trips.
	const isPlane = (r: any) => isService(r, ServiceTypes.Plane)
	const isHotel = (r: any) => isService(r, ServiceTypes.Hotel)

	function cityLabel(
		airport:
			| { iataCode: string; name: string; cityName: string | null }
			| null
			| undefined
	) {
		if (!airport) return null
		return airport.cityName ?? airport.name
	}

	const planeServices = computed(() => services.value.filter(isPlane))
	const hotelServices = computed(() => services.value.filter(isHotel))
	const otherServices = computed(() =>
		services.value.filter((r: any) => !isPlane(r) && !isHotel(r))
	)

	const handleUnlink = () => {
		showDismissWarning.value = false
		emit('unlink')
	}
</script>

<template>
	<div
		v-if="services.length"
		:class="[
			'rounded-xl border-2 overflow-hidden',
			serviceConfig.borderColor,
			serviceConfig.bgColor,
		]"
	>
		<!-- Header with service info -->
		<div class="flex items-start gap-3 px-4 py-3">
			<div
				:class="[
					'flex-shrink-0 mt-0.5 h-8 w-8 rounded-full flex items-center justify-center',
					serviceConfig.iconBgColor,
				]"
			>
				<span
					:class="[
						'material-symbols-outlined text-[18px]',
						serviceConfig.iconTextColor,
					]"
				>
					{{ serviceConfig.icon }}
				</span>
			</div>

			<div class="flex-1 min-w-0">
				<p :class="['text-sm font-semibold', serviceConfig.textColor]">
					{{ serviceConfig.message }}
				</p>
				<p class="text-[11px] text-gray-500 mt-0.5">
					Los datos del itinerario se usarán para pre-completar el formulario.
				</p>

				<!-- Vuelos -->
				<div class="mt-2 space-y-2">
					<div
						v-for="req in planeServices"
						:key="req.id"
						class="flex items-start gap-2"
					>
						<div class="min-w-0 flex-1">
							<p
								class="text-sm font-semibold text-gray-800 flex items-center gap-2"
							>
								<span class="material-symbols-outlined text-lg">
									plane_contrails
								</span>

								{{ req.originAirport?.cityName }}
								→
								{{ req.destinationAirport?.cityName ?? req.destination ?? '—' }}
							</p>

							<p class="text-[11px] text-gray-500">
								{{ req.suppliableName }} · {{ fmtDate(req.startDate) }} —
								{{ fmtDate(req.endDate) }}
							</p>
						</div>
					</div>

					<!-- Hoteles -->
					<div
						v-for="req in hotelServices"
						:key="req.id"
						class="flex items-start gap-2"
					>
						<div class="min-w-0 flex-1">
							<p
								class="truncate text-sm font-semibold text-gray-800 flex items-center"
							>
								<span class="material-symbols-outlined text-lg"> hotel </span>

								{{ req.suppliableName }}
							</p>
							<p
								v-if="req.suppliable?.address"
								class="truncate text-[11px] text-gray-400"
							>
								{{ req.suppliable.address }}
							</p>
							<p class="text-[11px] text-gray-500">
								Check in: {{ fmtDate(req.startDate) }} — Check out:
								{{ fmtDate(req.endDate) }}
							</p>
						</div>
					</div>

					<!-- Otros — chips -->
					<div
						v-if="otherServices.length"
						class="flex flex-wrap gap-1.5 pt-0.5"
					>
						<div
							v-for="req in otherServices"
							:key="req.id"
							class="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 shadow-sm"
						>
							<span class="material-symbols-outlined text-[12px] text-gray-400"
								>luggage</span
							>
							<div class="leading-tight">
								<p class="text-[11px] font-semibold text-gray-700">
									{{ req.serviceName
									}}<span
										v-if="req.suppliableName"
										class="font-normal text-gray-400"
									>
										· {{ req.suppliableName }}</span
									>
								</p>
								<p class="text-[10px] text-gray-400">
									{{ fmtDate(req.startDate) }} — {{ fmtDate(req.endDate) }}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Dismiss button -->
			<button
				type="button"
				:class="[
					'flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full hover:transition-colors',
					serviceConfig.accentColor,
					'hover:bg-opacity-20 hover:bg-current hover:text-opacity-80',
				]"
				@click="showDismissWarning = !showDismissWarning"
			>
				<span class="material-symbols-outlined text-[18px]">close</span>
			</button>
		</div>

		<!-- Member and Traveler Info Section -->
		<div
			v-if="loadingMember || member || travelers?.length"
			:class="[
				'mx-4 mb-3 rounded-lg border divide-y overflow-hidden',
				serviceConfig.borderColor,
			]"
		>
			<!-- Organizador row -->
			<div class="flex items-center gap-2.5 px-3 py-2 text-xs">
				<span
					:class="[
						'material-symbols-outlined text-[15px] flex-shrink-0',
						serviceConfig.iconTextColor,
					]"
					>badge</span
				>
				<span
					class="w-20 flex-shrink-0 text-[10px] font-bold uppercase tracking-wider text-gray-400"
					>Organizador</span
				>
				<template v-if="loadingMember && !member">
					<div class="h-3 w-32 animate-pulse rounded bg-gray-200" />
					<div class="h-3 w-24 animate-pulse rounded bg-gray-200" />
				</template>
				<span
					v-else
					class="truncate text-gray-700"
				>
					{{
						member ?
							`${member.fullName || `${member.name} ${member.lastname}`} · ${member.email}`
						:	'—'
					}}
				</span>
			</div>

			<!-- Viajeros rows -->
			<div
				v-if="travelers?.length"
				class="flex items-start gap-2.5 px-3 py-2 text-xs"
			>
				<span
					:class="[
						'material-symbols-outlined text-[15px] flex-shrink-0 mt-0.5',
						serviceConfig.iconTextColor,
					]"
					>group</span
				>
				<span
					class="w-20 flex-shrink-0 text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-0.5"
					>Viajero{{ travelers.length > 1 ? 's' : '' }}</span
				>
				<div class="flex flex-col gap-0.5 min-w-0">
					<span
						v-for="traveler in travelers"
						:key="traveler.id"
						class="truncate text-gray-700"
					>
						{{ traveler.fullName || `${traveler.name} ${traveler.lastname}` }}
						<span class="text-gray-400">· {{ traveler.email }}</span>
					</span>
				</div>
			</div>
		</div>

		<!-- Dismiss confirmation -->
		<Transition
			enter-active-class="transition-all duration-200 ease-out"
			enter-from-class="opacity-0 max-h-0"
			enter-to-class="opacity-100 max-h-40"
			leave-active-class="transition-all duration-150 ease-in"
			leave-from-class="opacity-100 max-h-40"
			leave-to-class="opacity-0 max-h-0"
		>
			<div
				v-if="showDismissWarning"
				class="mx-4 mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
			>
				<div class="flex items-start gap-2">
					<span
						class="material-symbols-outlined text-[16px] text-amber-600 flex-shrink-0 mt-0.5"
					>
						warning
					</span>
					<div class="flex-1">
						<p class="text-sm text-amber-800">
							Al cerrar este aviso, la reserva se creará como un
							<strong>itinerario completamente nuevo</strong> e independiente.
						</p>

						<div class="mt-2.5 flex items-center gap-2">
							<button
								type="button"
								class="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 transition-colors"
								@click="handleUnlink"
							>
								Sí, desvincular
							</button>

							<button
								type="button"
								class="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 transition-colors"
								@click="showDismissWarning = false"
							>
								Cancelar
							</button>
						</div>
					</div>
				</div>
			</div>
		</Transition>
	</div>
</template>
